import React, { useMemo } from 'react';
import { Grid, Button, Typography, AlertColor } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import { GroupTypeDetails } from 'graphql/types';
import { SimpleDropDown } from './SimpleDropDown';
import { EnabledDisabled } from './constant';
import { GroupSelectionRow } from './GroupSelectionRow';
import { ToastSnackBar } from './ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  container: () => ({
    marginTop: theme.spacing(2.5),
    alignContent: 'flex-start',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column'
  }),
  content: {
    display: 'flex',
    borderRadius: theme.spacing(0, 1.25, 0, 0),
    alignContent: 'flex-start'
  },
  dropDown: {
    marginRight: theme.spacing(1)
  },
  text: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  button: {
    marginLeft: theme.spacing(2),
    textTransform: 'none'
  },
  buttonText: {
    fontWeight: theme.typography.fontWeightMedium
  },
  submitButton: {
    display: 'flex',
    alignContent: 'flex-start',
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 4)
  }
}));
type SettingBodyProps = {
  enabledGroupTypes: GroupTypeDetails[];
  fields: Record<string, any>[];
  handleRemove: (index?: number | number[] | undefined) => void;
  handleClick: () => void;
  show: boolean;
  category: AlertColor | undefined;
  message: string | undefined;
  isDirty: boolean;
  saving: boolean;
  fieldName: string;
  defaultRowValue: EnabledDisabled | undefined;
};

export const SettingBody: React.FC<SettingBodyProps> = ({
  enabledGroupTypes,
  fields,
  handleRemove,
  handleClick,
  show,
  category,
  message,
  isDirty,
  saving,
  fieldName,
  defaultRowValue
}: SettingBodyProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    return [
      {
        key: EnabledDisabled.enabled,
        value: formatMessage({ id: 'settings.enabledDisabled.enabled' })
      },
      {
        key: EnabledDisabled.disabled,
        value: formatMessage({ id: 'settings.enabledDisabled.disabled' })
      }
    ];
  }, [formatMessage]);

  return (
    <Grid
      container
      className={classes.container}
      data-testid="setting-content-page-start-end"
    >
      {fields.map((field, index) => (
        <GroupSelectionRow
          key={field.id}
          options={options}
          enabledGroupTypes={enabledGroupTypes}
          rowID={`${fieldName}.${index}`}
          rowIndex={index}
          field={field}
          onRemove={handleRemove}
        />
      ))}

      <div className={classes.content}>
        <div className={classes.dropDown}>
          <SimpleDropDown
            value={defaultRowValue}
            options={options}
            rowID={'defaultRow'}
          />
        </div>
        <Typography display="inline" className={classes.text} variant="body2">
          <FormattedMessage id="settings.defaultValue" />
        </Typography>
        <Button
          className={classes.button}
          size="small"
          color="primary"
          onClick={handleClick}
          data-testid={'add-target-button'}
          disabled={!enabledGroupTypes || enabledGroupTypes!.length === 0}
        >
          <AddOutlinedIcon fontSize="inherit" />
          <Typography
            display="inline"
            className={classes.buttonText}
            variant="body2"
          >
            <FormattedMessage id="settings.addTargetButton" />
          </Typography>
        </Button>
      </div>
      <div>
        <Button
          className={classes.submitButton}
          disabled={saving || !isDirty}
          size="large"
          color="primary"
          variant="contained"
          type="submit"
          data-testid="save-settings-button"
        >
          <FormattedMessage id="settings.save" />
        </Button>
        <ToastSnackBar show={show} category={category} message={message} />
      </div>
    </Grid>
  );
};
